import { Routes } from '@angular/router';
import { RoutingSegment } from '../../../../libs/common/entities/routing-segment';
import { SplashGuardService } from '../../../../libs/guard/splash-guard.service';
import { LoginGuardService, MemberGuardService, RoleGuard, RootGuardService } from '../../../../libs/guard';
import { NoteCharacteristicAuthGuard } from '../../../../libs/note/guards/note-tags.guard';
import { PageName } from '../../../../libs/config/entities';
import { RedirectGuard } from '../../../../libs/guard/redirect-guard.service';
import { homePatientPageGuard, kioskGuard } from '../../../../libs/common/pages/home-patient/home-patient-page.guard';
import { patientGuard } from '../../../../libs/guard/patient.guard';
import { UserIdentityResolver } from '../../../../libs/user/user-identity/user-identity.resolver';
import { ShopFeatureGuard } from '../../../../libs/guard/shop-feature.guard';
import { RegisterFeatureGuard } from '../../../../libs/guard/registrer.guard';
import { profileGuard } from '../../../../libs/guard/profile.guard';
import { courseGuard } from '../../../../libs/guard/course.guard';
import { configurationGuard } from '../../../../libs/guard/configuration.guard';
import { InfoPageGuard } from '../../../../libs/guard/info-page.guard';
import { TaskFeatureGuard } from '../../../../libs/guard/task-feature.guard';
import { TermsAndConditionsConfigurationGuard } from '../../../../libs/guard/terms-and-conditions.guard';
import { ProgramFeatureGuard } from '../../../../libs/guard/program-feature.guard';
import { trainingGuard } from '../../../../libs/guard/training.guard';

export const routes: Routes = [
    {
        path: '',
        canActivate: [RootGuardService],
        loadComponent: () => import('../../../../libs/common/pages/default/default.page'),
    },
    {
        path: 'login-callback',
        loadComponent: () => import('../../../../libs/authentication/login-callback/login-callback.page'),
    },
    {
        path: RoutingSegment.LOGIN,
        canActivate: [LoginGuardService],
        loadComponent: () => import('../../../../libs/authentication/login/login.page'),
    },
    {
        path: RoutingSegment.COMMON,
        canActivate: [SplashGuardService],
        data: {
            title: 'COMMON.MAIN',
            configuration: { showSideMenu: true, showToolbar: false },
            pageId: `${PageName.COMMON}_${PageName.MAIN}`,
            categories: [RoutingSegment.COMMON],
            roles: [],
        },
        children: [
            {
                path: RoutingSegment.MANUAL,
                canActivate: [RedirectGuard],
                data: {
                    externalUrl: 'https://handbuch.ztm.de/curafida-ueberblick',
                },
                loadComponent: () => import('../../../../libs/common/pages/default/default.page'),
            },
            {
                path: RoutingSegment.IMPRINT,
                data: { title: 'COMMON.IMPRINT' },
                loadComponent: () => import('../../../../libs/common/pages/imprint/imprint.page'),
            },
            {
                path: RoutingSegment.USER_AGREEMENT,
                data: { title: 'COMMON.USER_AGREEMENT' },
                loadComponent: () => import('../../../../libs/common/pages/user-agreement/user-agreement.page'),
            },
            {
                path: RoutingSegment.TERMS_AND_CONDITIONS,
                data: { title: 'COMMON.TERMS_AND_CONDITIONS' },
                canActivate: [TermsAndConditionsConfigurationGuard],
                loadComponent: () =>
                    import('../../../../libs/common/pages/terms-and-conditions/terms-and-conditions.page'),
            },
            {
                path: RoutingSegment.PRIVACY,
                data: { title: 'COMMON.PRIVACY' },
                loadComponent: () => import('../../../../libs/common/pages/privacy/privacy.page'),
            },
            {
                path: RoutingSegment.CONFORMITY,
                data: { title: 'COMMON.CONFORMITY' },
                loadComponent: () => import('../../../../libs/common/pages/conformity/conformity.page'),
            },
            {
                path: RoutingSegment.CONTACT,
                data: { title: 'COMMON.CONTACT' },
                loadComponent: () => import('../../../../libs/common/pages/contact/contact.page'),
            },
        ],
    },
    {
        path: RoutingSegment.INFO,
        canActivate: [InfoPageGuard],
        loadComponent: () => import('../../../../libs/common/pages/info/info.page'),
    },
    {
        path: RoutingSegment.SHOP,
        canActivate: [ShopFeatureGuard],
        loadChildren: () => import('../../../../libs/shop/shop.routes').then((m) => m.shopRoutes),
    },
    {
        path: RoutingSegment.REGISTER,
        canActivate: [RegisterFeatureGuard],
        loadChildren: () => import('../../../../libs/user/registration.routes').then((m) => m.registrationRoutes),
    },

    {
        path: RoutingSegment.MEMBER,
        canActivate: [MemberGuardService],
        children: [
            {
                path: RoutingSegment.HOME,
                loadComponent: () => import('../../../../libs/common/pages/home/home.page'),
                canActivate: [RoleGuard],
            },
            {
                path: RoutingSegment.SHOP,
                canActivate: [ShopFeatureGuard],
                loadChildren: () => import('../../../../libs/shop/shop.routes').then((c) => c.shopRoutes),
            },
            {
                path: RoutingSegment.MEETINGS,
                loadChildren: () => import('../../../../libs/meeting/meetingRoutes').then((c) => c.meetingRoutes),
                canActivate: [courseGuard],
            },
            {
                path: RoutingSegment.KIOSK,
                canActivate: [patientGuard, kioskGuard],
                loadComponent: () => import('../../../../libs/kiosk/pages/home-page/kiosk-home-page.component'),
            },
            {
                path: `${RoutingSegment.CONSULTATION_MANAGEMENT}/new`,
                canActivate: [RoleGuard],
                loadComponent: () =>
                    import('../../../../libs/consultation/pages/consultation-creation/consultation-creation.page'),
            },
            {
                path: `${RoutingSegment.ORDER_MANAGEMENT}/new`,
                canActivate: [RoleGuard],
                loadComponent: () => import('../../../../libs/order/pages/order-creation/order-creation.page'),
            },
            {
                path: `${RoutingSegment.SCREENING_MANAGEMENT}/new`,
                loadComponent: () =>
                    import('../../../../libs/screening/pages/screening-creation/screening-creation.page'),
            },
            {
                path: RoutingSegment.PATIENT_MANAGEMENT,
                children: [
                    {
                        path: RoutingSegment.HOME,
                        loadComponent: () =>
                            import('../../../../libs/common/pages/home-patient/home-patient-page.component'),
                        data: { title: 'JOURNAL.PAGE_TITLE' },
                        canActivate: [homePatientPageGuard],
                    },
                    {
                        path: `${RoutingSegment.CONSULTATION_MANAGEMENT}/${RoutingSegment.DETAIL}/:consultationId`,
                        loadComponent: () =>
                            import('../../../../libs/consultation/pages/consultation-detail/consultation-detail.page'),
                    },
                    {
                        path: `${RoutingSegment.ORDER_MANAGEMENT}/${RoutingSegment.DETAIL}/:orderId`,
                        loadComponent: () => import('../../../../libs/order/pages/order-detail/order-detail.page'),
                    },
                    {
                        path: RoutingSegment.TRAINING,
                        canActivate: [trainingGuard],
                        loadChildren: () =>
                            import('../../../../libs/therapy/training-routing.module').then(
                                (m) => m.PatientTrainingRoutingModule,
                            ),
                    },
                    {
                        path: `${RoutingSegment.MEASUREMENT_MANAGEMENT}/${RoutingSegment.NORM_VALUES}/:userUuid`,
                        loadComponent: () =>
                            import('../../../../libs/measurement/pages/norm-values/norm-value-list.page').then(
                                (m) => m.NormValueListPage,
                            ),
                        canActivate: [RoleGuard],
                        resolve: { userIdentity: UserIdentityResolver },
                    },
                    {
                        path: RoutingSegment.PROGRAM_MANAGEMENT,
                        loadChildren: () =>
                            import('../../../../libs/therapy/program-routing.module').then(
                                (m) => m.ProgramRoutingModule,
                            ),
                        canActivate: [ProgramFeatureGuard],
                    },
                ],
            },
            {
                path: RoutingSegment.PATIENT_MANAGEMENT,
                loadChildren: () => import('../../../../libs/user/patient-routing.module').then((m) => m.patientRoutes),
            },
            {
                path: RoutingSegment.PROFILE,
                loadComponent: () => import('../../../../libs/common/pages/profile/profile.page'),
                canActivate: [RoleGuard, profileGuard],
            },
            {
                path: RoutingSegment.CONCEPT,
                loadChildren: () =>
                    import('../../../../libs/therapy/manual-routing.module').then((m) => m.administrationRoutes),
            },
            {
                path: RoutingSegment.KNOWLEDGE,
                children: [
                    {
                        path: `${RoutingSegment.MAIN}`,
                        loadComponent: () =>
                            import('../../../../libs/knowledge/pages/member/technologies/technologies-page.component'),
                        canActivate: [RoleGuard],
                    },
                    {
                        path: `${RoutingSegment.DETAIL}/:id`,
                        loadComponent: () =>
                            import('../../../../libs/knowledge/pages/member/technology-detail/technology-detail-page.component'),
                        canActivate: [RoleGuard],
                    },
                ],
            },
            {
                path: RoutingSegment.ADMINISTRATION,
                children: [
                    {
                        path: RoutingSegment.USER,
                        loadChildren: () => import('../../../../libs/user/user.routes').then((m) => m.userRoutes),
                    },
                    {
                        path: RoutingSegment.CONFIGURATION,
                        canActivate: [configurationGuard],
                        loadComponent: () => import('../../../../libs/config/pages/configuration/configuration.page'),
                    },
                    {
                        path: RoutingSegment.ORGANISATION_MANAGEMENT,
                        children: [
                            {
                                path: RoutingSegment.LIST,
                                loadComponent: () =>
                                    import('../../../../libs/user/pages/organization-management/organization/organization-list.page'),
                                canActivate: [RoleGuard],
                            },
                            {
                                path: `${RoutingSegment.ORGANISATION}/:groupPath`,
                                loadComponent: () =>
                                    import('../../../../libs/user/pages/organization-management/group/organization-detail/organization-detail.page'),
                                canActivate: [RoleGuard],
                            },
                            {
                                path: `${RoutingSegment.GROUP}/:groupPath`,
                                loadComponent: () =>
                                    import('../../../../libs/user/pages/organization-management/group/group-detail/group-detail.page'),
                                canActivate: [RoleGuard],
                            },
                        ],
                    },
                    {
                        path: RoutingSegment.TRAINING,
                        canActivate: [trainingGuard],
                        loadChildren: () =>
                            import('../../../../libs/therapy/training-routing.module').then(
                                (m) => m.AdministrationTrainingRoutingModule,
                            ),
                    },
                    {
                        path: RoutingSegment.TASK,
                        canActivate: [TaskFeatureGuard],
                        loadChildren: () =>
                            import('../../../../libs/therapy/task-routing.module').then(
                                (m) => m.AdministrationTaskRoutingModule,
                            ),
                    },
                    {
                        path: RoutingSegment.QUESTIONNAIRE,
                        loadChildren: () =>
                            import('../../../../libs/therapy/questionnaire-administration.routes').then(
                                (m) => m.questionnaireAdministrationRoutes,
                            ),
                    },
                    {
                        path: RoutingSegment.PROGRAM_MANAGEMENT,
                        loadChildren: () =>
                            import('../../../../libs/program/program-template-routing.module').then(
                                (c) => c.administrationProgramRoutes,
                            ),
                        canActivate: [ProgramFeatureGuard],
                    },
                    {
                        path: '',
                        loadChildren: () =>
                            import('../../../../libs/meeting/meetingRoutes').then((c) => c.meetingAdministrationRoutes),
                        canActivate: [courseGuard],
                    },
                    {
                        path: RoutingSegment.NOTE_MANAGEMENT,
                        canActivate: [NoteCharacteristicAuthGuard],
                        loadComponent: () =>
                            import('../../../../libs/note/pages/administration/note-administration.page'),
                    },
                ],
            },
            {
                path: RoutingSegment.VIDEO_CONFERENCE,
                loadChildren: () =>
                    import('../../../../libs/video-conference/video-conference.routes').then(
                        (m) => m.videoConferenceRoutes,
                    ),
            },
        ],
    },
    {
        path: RoutingSegment.DEFAULT,
        canActivate: [SplashGuardService],
        loadComponent: () => import('../../../../libs/common/pages/default/default.page'),
    },
    {
        path: '**',
        canActivate: [SplashGuardService],
        loadComponent: () => import('../../../../libs/common/pages/default/default.page'),
    },
];
